import React from 'react';

export type WatchdogViewMode = 'months' | 'days';

const MODES: {
  id: WatchdogViewMode;
  label: string;
  hint: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'months',
    label: 'ماه‌ها',
    hint: 'نمای ماهانه',
    icon: (
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden>
        <path d="M4 4h12v12H4V4zm1.5 1.5v9h9v-9h-9z" />
        <path d="M6.5 7h7v1.2h-7V7zm0 2.4h5v1.2h-5V9.4zm0 2.4h7v1.2h-7v-1.2z" />
      </svg>
    ),
  },
  {
    id: 'days',
    label: 'روزها',
    hint: 'نمای روزانه',
    icon: (
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden>
        <path d="M6 2v1.5H4.5a1.5 1.5 0 00-1.5 1.5V16a1.5 1.5 0 001.5 1.5h11a1.5 1.5 0 001.5-1.5V5a1.5 1.5 0 00-1.5-1.5H14V2h-1.5v1.5H7.5V2H6zm-2 4.5h12V16h-12V6.5z" />
        <path d="M7 9h1.8v1.6H7V9zm3 0H12v1.6h-2V9zm3 0h1.8v1.6H13V9zM7 12h1.8v1.6H7V12zm3 0H12v1.6h-2V12z" />
      </svg>
    ),
  },
];

interface Props {
  value: WatchdogViewMode;
  onChange: (mode: WatchdogViewMode) => void;
}

const WatchdogViewSwitcher: React.FC<Props> = ({ value, onChange }) => {
  const active = MODES.find((m) => m.id === value);

  return (
    <div className="space-y-2">
      <div
        dir="rtl"
        className="relative inline-grid w-[13.5rem] grid-cols-2 overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-l from-slate-50 via-white to-indigo-50/80 p-1 shadow-inner shadow-indigo-100/60"
        role="tablist"
        aria-label="سطح زوم تقویم"
      >
        <span
          className="pointer-events-none absolute inset-y-1 right-1 w-[calc(50%-0.25rem)] rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 shadow-md shadow-indigo-500/25 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{
            transform: value === 'months' ? 'translateX(0)' : 'translateX(-100%)',
          }}
          aria-hidden
        />
        {MODES.map((mode) => {
          const selected = value === mode.id;
          return (
            <button
              key={mode.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(mode.id)}
              className={`relative z-[1] flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-colors duration-300 ${
                selected ? 'text-white' : 'text-slate-600 hover:text-indigo-700'
              }`}
            >
              <span className={selected ? 'text-white/95' : 'text-indigo-500'}>{mode.icon}</span>
              {mode.label}
            </button>
          );
        })}
      </div>
      {active ? (
        <p className="text-xs text-gray-500">
          {active.hint}
          {' — '}
          {value === 'months'
            ? 'کلیک روی ماه = جزئیات روز و خدمت'
            : 'کلیک روی روز = جزئیات ساعت و خدمت'}
        </p>
      ) : null}
    </div>
  );
};

export default WatchdogViewSwitcher;
